// src/components/products/CreateProduct.jsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import ProductSettings from './ProductSettings';
import ScanProduct from './ScanProduct';
import BulkUploadFlow from './BulkUploadFlow';
// Constants for better maintainability
const LISTING_TYPES = {
  PRODUCT: 'product',
  SERVICE: 'service',
  SCAN_PRODUCT: "scan_product",
  BULK_UPLOAD: "bulk_upload"
};

const PRODUCT_TYPES = {
  PHYSICAL: 'physical',
  DIGITAL: 'digital',
  SCAN_PRODUCT: 'scan_product'
};

const DIGITAL_PRODUCT_TYPES = {
  EBOOK: 'ebook',
  ART_COLLECTION: 'art_collection',
  OTHER: 'other'
};

const ALLOWED_FILE_TYPES = {
  'application/pdf': 'pdf',
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'application/zip': 'zip',
  'application/x-zip-compressed': 'zip'
};

const MAX_FILE_SIZES = {
  'pdf': 50 * 1024 * 1024, // 50MB
  'image': 30 * 1024 * 1024, // 20MB
  'zip': 100 * 1024 * 1024 // 100MB
};

const DELIVERY_TIMELINES = [
  { value: '1', label: '1 day' },
  { value: '2', label: '2 days' },
  { value: '3', label: '3 days' },
  { value: '7', label: '1 week' },
  { value: '14', label: '14 days' },
  { value: '21', label: '21 days' }
];

const RETURN_POLICIES = [
  { value: '0', label: 'No returns accepted' },
  { value: '7', label: '7 days return policy' },
  { value: '14', label: '14 days return policy' },
  { value: '30', label: '30 days return policy' }
];

const CONDITIONS = [
  { value: 'new', label: 'Brand New' },
  { value: 'used', label: 'Used - Like New' },
  { value: 'refurbished', label: 'Refurbished' }
];

const VARIATION_TYPES = ['Size', 'Color', 'Material', 'Style', 'Pattern'];

// Move VariationsSection outside the main component to prevent recreation
const VariationsSection = React.memo(({ 
  listingType, 
  currentVariation, 
  variations, 
  handleVariationTypeChange, 
  handleVariationValueChange, 
  handleVariationPriceChange, 
  handleVariationInventoryChange, 
  addVariation, 
  removeVariation,
  formatNumberWithCommas 
}) => (
  listingType === LISTING_TYPES.PRODUCT && (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Variations</h3>
      <p className="text-gray-600 mb-6 text-sm">Add variations like sizes, colors, materials, etc.</p>
      
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={currentVariation.type}
            onChange={handleVariationTypeChange}
          >
            <option value="">Select type</option>
            {VARIATION_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Value</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={currentVariation.value}
            onChange={handleVariationValueChange}
            placeholder="e.g., Red, Large"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Price Diff (₦)</label>
          <input
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={currentVariation.price}
            onChange={handleVariationPriceChange}
            placeholder="0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Inventory</label>
          <input
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={currentVariation.inventory}
            onChange={handleVariationInventoryChange}
            placeholder="0"
          />
        </div>

        <div className="flex items-end">
          <button
            type="button"
            onClick={addVariation}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center text-sm"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
            </svg>
            Add
          </button>
        </div>
      </div>

      {variations.length > 0 && (
        <div className="space-y-3 mt-8">
          <h4 className="text-base font-semibold text-gray-900">Added Variations ({variations.length})</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-white">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price Diff (₦)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inventory</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {variations.map((v) => (
                  <tr key={v.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{v.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{v.value}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{v.price ? formatNumberWithCommas(v.price) : '0'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{v.inventory || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        type="button"
                        onClick={() => removeVariation(v.id)}
                        className="text-red-600 hover:text-red-900"
                        aria-label={`Remove ${v.type}: ${v.value}`}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
));

// Image Upload Component with Progress
const ImageUploadWithProgress = ({ 
  imagePreviews, 
  uploadProgresses, 
  uploadErrors, 
  isUploadingImages, 
  handleImageChange, 
  removeImage 
}) => {
  const fileInputRef = useRef(null);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <label className="block text-sm font-medium text-gray-900 mb-4">
        Product Images (Multiple allowed)
      </label>
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-6">
        <div className="flex-1">
          <label className="block">
            <span className="sr-only">Choose images</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg, .png, .jpeg, .webp"
              multiple 
              onChange={handleImageChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-colors"
              disabled={isUploadingImages}
            />
          </label>
          <p className="text-gray-500 mt-2 text-sm">
            JPG, PNG, JPEG or WebP (Max 5MB each). Upload up to 10 images.
          </p>
        </div>
      </div>

      {/* Image Preview Grid with Upload Progress */}
      <div className="flex flex-wrap gap-3">
        {imagePreviews.map((preview, index) => (
          <div key={preview.id} className="relative shrink-0">
            <div className="relative">
              <img
                className="h-24 w-24 object-cover rounded-lg border-2 border-gray-300"
                src={preview.previewUrl}
                alt={`Product image ${index + 1}`}
              />
              
              {/* Upload Progress Overlay */}
              {uploadProgresses[preview.id] > 0 && uploadProgresses[preview.id] < 100 && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                  <div className="text-white text-center">
                    <div className="w-12 h-12 mx-auto mb-1 relative">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold">{uploadProgresses[preview.id]}%</span>
                      </div>
                      <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="16" fill="none" stroke="#e5e7eb" strokeWidth="2"/>
                        <circle cx="18" cy="18" r="16" fill="none" stroke="#1e5aff" strokeWidth="2" 
                          strokeDasharray="100" 
                          strokeDashoffset={100 - uploadProgresses[preview.id]}
                          strokeLinecap="round"/>
                      </svg>
                    </div>
                    <span className="text-xs">Uploading...</span>
                  </div>
                </div>
              )}
              
              {/* Upload Error */}
              {uploadErrors[preview.id] && (
                <div className="absolute inset-0 bg-red-100 border border-red-300 rounded-lg flex items-center justify-center p-2">
                  <div className="text-center">
                    <svg className="w-6 h-6 text-red-500 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-red-700 font-medium">Upload Failed</span>
                  </div>
                </div>
              )}
              
              {/* Upload Success */}
              {uploadProgresses[preview.id] === 100 && !uploadErrors[preview.id] && (
                <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
            
            <button
              type="button"
              onClick={() => removeImage(preview.id)}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              aria-label="Remove image"
              disabled={isUploadingImages}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        ))}

        {/* Placeholder if no images are selected */}
        {imagePreviews.length === 0 && (
          <div className="h-24 w-full border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center bg-white text-gray-500 text-sm">
            <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            No images uploaded yet.
          </div>
        )}
      </div>

      {/* Global Upload Status */}
      {isUploadingImages && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <svg className="animate-spin w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2v4m0 12v4m8-10h-4M6 12H2m15.364-7.364l-2.828 2.828M7.464 17.536l-2.828 2.828m12.728 0l-2.828-2.828M7.464 6.464L4.636 3.636" />
            </svg>
            <span className="text-sm text-blue-700 font-medium">
              Uploading {Object.values(uploadProgresses).filter(p => p > 0 && p < 100).length} images...
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const CreateProduct = ({ onProductCreated }) => {
  const [listingType, setListingType] = useState(null);
  const [showScanChoice, setShowScanChoice] = useState(false);
  const [productType, setProductType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    deliveryTimeline: '',
    inventory: '',
    sku: '',
    weight: '',
    dimensions: '',
    brand: '',
    condition: 'new',
    returnPolicy: '7',
    imageUrls: [], // Store uploaded image URLs instead of files
    upfrontPaymentPercentage: 0,
    acceptNegotiations: false,
    minPrice: '',
    maxPrice: '',
    availableForPickup: true,
    availableForDelivery: false,
    deliveryOption: '',
    lagosDeliveryFee: '',
    outsideLagosDeliveryFee: '',
    // Digital product fields
    isDigital: false,
    digitalProductType: '',
    digitalFile: null,
    digitalFileUrl: '',
    allowedDownloads: 3,
    cloudinaryPublicId: '',
    downloadExpiryDays: 30,
    watermarkEnabled: false
  });
  
  const [variations, setVariations] = useState([]);
  const [currentVariation, setCurrentVariation] = useState({
    type: '',
    value: '',
    price: '',
    inventory: '10',
    sku: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // Enhanced image state management
  const [imagePreviews, setImagePreviews] = useState([]); // { id, previewUrl, file }
  const [uploadProgresses, setUploadProgresses] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  
  const [digitalFilePreview, setDigitalFilePreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showTimelineDropdown, setShowTimelineDropdown] = useState(false);
  const [hasUploadedFile, setUploadeFile] = useState(false);
  
  // Digital file upload states
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  const digitalFileInputRef = useRef(null);
  const categoryInputRef = useRef(null);
  const timelineRef = useRef(null);

  // Memoized values
  const selectedTimelineLabel = useMemo(() => {
    const selected = DELIVERY_TIMELINES.find(t => t.value === formData.deliveryTimeline);
    return selected ? selected.label : 'Select delivery timeline';
  }, [formData.deliveryTimeline]);

  // Data fetching
  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.post(API_CONFIG.PRODUCTS.COLLECTIONS);
      const data = response.data;
      
      if (data.success) {
        setCategories(data.collections || []);
        setFilteredCategories(data.collections || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      showToast('Failed to load categories', 'error');
    }
  }, []);

  // Formatting helpers
  const formatNumberWithCommas = useCallback((number) => {
    if (!number) return '';
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }, []);

  // Digital file validation
  const validateDigitalFile = useCallback((file) => {
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    const fileType = ALLOWED_FILE_TYPES[file.type];
    if (!fileType) {
      return { 
        valid: false, 
        error: 'Invalid file type. Only PDF, images (JPEG, PNG, GIF, WebP), and ZIP files are allowed.' 
      };
    }

    const maxSize = MAX_FILE_SIZES[fileType];
    if (file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      return { 
        valid: false, 
        error: `File too large. Maximum size for ${fileType.toUpperCase()} files is ${maxSizeMB}MB.` 
      };
    }

    return { valid: true, fileType };
  }, []);

  // Handle digital file upload to backend
  const handleDigitalFileUpload = useCallback(async (file) => {
    if (!file) return null;

    // Reset previous state
    setUploadError('');
    setIsUploading(true);
    setUploadProgress(0);
    setDigitalFilePreview(null);

    const abortController = new AbortController();

    try {
      setUploadProgress(10);
      
      const fileValidation = validateDigitalFile(file);
      if (!fileValidation.valid) {
        throw new Error(fileValidation.error);
      }

      // Create preview for images
      if (fileValidation.fileType === 'image') {
        const previewUrl = URL.createObjectURL(file);
        setDigitalFilePreview(previewUrl);
      }

      setUploadProgress(20);

      // Prepare upload data
      const uploadData = new FormData();
      uploadData.append('file', file);

      setUploadProgress(30);

      // Upload to backend
      const uploadResponse = await api.post(API_CONFIG.PRODUCTS.UPLOAD, uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000,
        signal: abortController.signal,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const uploadPercentage = (progressEvent.loaded / progressEvent.total) * 60;
            setUploadProgress(30 + uploadPercentage);
          } else {
            setUploadProgress(prev => {
              const newProgress = prev + (100 - prev) * 0.1;
              return Math.min(newProgress, 90);
            });
          }
        },
      });

      setUploadProgress(95);

      const uploadResult = uploadResponse.data;
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || uploadResult.message || 'Upload failed');
      }

      const fileUrl = uploadResult.url || uploadResult.fileUrl || uploadResult.secure_url;
      const publicId = uploadResult.cloudinary_public_id;

      if (!fileUrl) {
        console.warn('Upload response missing URL:', uploadResult);
        throw new Error('Upload completed but no file URL returned');
      }

      setUploadProgress(100);
      setUploadeFile(true);
      setFormData(prev => ({ 
        ...prev, 
        digitalFile: file,
        digitalFileUrl: fileUrl,
        fileType: file.type,
        fileSize: file.size,
        cloudinaryPublicId: publicId,
      }));

      showToast(`File uploaded successfully!`, 'success');
      
      setTimeout(() => {
        setUploadProgress(0);
        setIsUploading(false);
      }, 1500);

      return fileUrl;

    } catch (error) {
      if (digitalFilePreview) {
        URL.revokeObjectURL(digitalFilePreview);
      }
      
      console.error('Digital file upload error:', error);
      
      let errorMessage = 'Failed to upload file. Please try again.';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Upload was cancelled.';
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage = 'Upload timed out. Please try a smaller file or check your connection.';
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large. Please choose a smaller file.';
      } else if (error.response?.status === 415) {
        errorMessage = 'File type not supported. Please check the file format.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setUploadError(errorMessage);
      showToast(errorMessage, 'error');
      setUploadProgress(0);
      setIsUploading(false);
      setDigitalFilePreview(null);
      
      return null;
    }
  }, [validateDigitalFile, digitalFilePreview]);

  // Enhanced image upload handler for physical products
  const handleImageUpload = useCallback(async (file, imageId) => {
    if (!file) return null;

    try {
      // Validate image file
      if (file.size > 5 * 1024 * 1024) {
        throw new Error(`Image "${file.name}" is too large (max 5MB).`);
      }
      if (!file.type.startsWith('image/')) {
        throw new Error(`File "${file.name}" is not an image.`);
      }

      // Update progress
      setUploadProgresses(prev => ({ ...prev, [imageId]: 10 }));

      // Prepare upload data
      const uploadData = new FormData();
      uploadData.append('file', file);

      setUploadProgresses(prev => ({ ...prev, [imageId]: 30 }));

      // Upload to backend
      const uploadResponse = await api.post(API_CONFIG.PRODUCTS.UPLOAD, uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const uploadPercentage = (progressEvent.loaded / progressEvent.total) * 60;
            setUploadProgresses(prev => ({ ...prev, [imageId]: 30 + uploadPercentage }));
          } else {
            setUploadProgresses(prev => {
              const currentProgress = prev[imageId] || 0;
              const newProgress = currentProgress + (100 - currentProgress) * 0.1;
              return { ...prev, [imageId]: Math.min(newProgress, 90) };
            });
          }
        },
      });

      setUploadProgresses(prev => ({ ...prev, [imageId]: 95 }));

      const uploadResult = uploadResponse.data;
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || uploadResult.message || 'Upload failed');
      }

      const imageUrl = uploadResult.url || uploadResult.fileUrl || uploadResult.secure_url;

      if (!imageUrl) {
        throw new Error('Upload completed but no image URL returned');
      }

      // Mark upload as complete
      setUploadProgresses(prev => ({ ...prev, [imageId]: 100 }));
      setUploadErrors(prev => ({ ...prev, [imageId]: null }));

      return imageUrl;

    } catch (error) {
      console.error(`Image upload error for ${file.name}:`, error);
      
      let errorMessage = 'Failed to upload image. Please try again.';
      
      if (error.response?.status === 413) {
        errorMessage = 'Image too large. Maximum size is 5MB.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setUploadErrors(prev => ({ ...prev, [imageId]: errorMessage }));
      setUploadProgresses(prev => ({ ...prev, [imageId]: 0 }));
      
      return null;
    }
  }, []);

  // Handle multiple image selection and upload
  const handleImageChange = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate total number of images
    const totalImages = imagePreviews.length + files.length;
    if (totalImages > 10) {
      showToast('Maximum 10 images allowed. Please select fewer images.', 'error');
      return;
    }

    // Create previews and prepare for upload
    const newPreviews = files.map(file => {
      const id = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      return {
        id,
        previewUrl: URL.createObjectURL(file),
        file
      };
    });

    setImagePreviews(prev => [...prev, ...newPreviews]);
    setIsUploadingImages(true);

    // Upload each image individually
    const uploadPromises = newPreviews.map(async (preview) => {
      const imageUrl = await handleImageUpload(preview.file, preview.id);
      
      if (imageUrl) {
        // Add the uploaded URL to formData
        setFormData(prev => ({
          ...prev,
          imageUrls: [...prev.imageUrls, imageUrl]
        }));
        return { success: true, id: preview.id, url: imageUrl };
      } else {
        return { success: false, id: preview.id };
      }
    });

    // Wait for all uploads to complete
    const results = await Promise.all(uploadPromises);
    
    const failedUploads = results.filter(result => !result.success);
    if (failedUploads.length > 0) {
      showToast(`${failedUploads.length} image(s) failed to upload. Please try again.`, 'error');
    }

    const successfulUploads = results.filter(result => result.success);
    if (successfulUploads.length > 0) {
      showToast(`${successfulUploads.length} image(s) uploaded successfully!`, 'success');
    }

    setIsUploadingImages(false);
  }, [imagePreviews.length, handleImageUpload]);

  // Remove image from previews and formData
  const removeImage = useCallback((imageIdToRemove) => {
    setImagePreviews(prev => {
      const updatedPreviews = prev.filter(preview => preview.id !== imageIdToRemove);
      // Clean up the object URL
      const removedPreview = prev.find(preview => preview.id === imageIdToRemove);
      if (removedPreview) {
        URL.revokeObjectURL(removedPreview.previewUrl);
      }
      return updatedPreviews;
    });

    // Also remove from formData imageUrls
    setFormData(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.filter(url => {
        // Since we don't have a direct mapping, we'll rely on the order
        // This is a limitation - in a real app you might want to store more metadata
        const previewIndex = imagePreviews.findIndex(p => p.id === imageIdToRemove);
        return previewIndex === -1 || prev.imageUrls[previewIndex] !== url;
      })
    }));

    // Clean up progress and error states
    setUploadProgresses(prev => {
      const newProgresses = { ...prev };
      delete newProgresses[imageIdToRemove];
      return newProgresses;
    });

    setUploadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[imageIdToRemove];
      return newErrors;
    });
  }, [imagePreviews]);

  // Event handlers (same as before, but updated for imageUrls)
  const handleCategorySelect = useCallback((categoryName) => {
    setFormData(prev => ({ ...prev, category: categoryName }));
    setShowCategoryDropdown(false);
  }, []);

  const handleTimelineSelect = useCallback((value) => {
    setFormData(prev => ({ ...prev, deliveryTimeline: value }));
    setShowTimelineDropdown(false);
  }, []);

  const handleDigitalFileChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validateDigitalFile(file);
    if (!validation.valid) {
      showToast(validation.error, 'error');
      return;
    }

    await handleDigitalFileUpload(file);
  }, [validateDigitalFile, handleDigitalFileUpload]);

  const removeDigitalFile = useCallback(() => {
    setFormData(prev => ({ 
      ...prev, 
      digitalFile: null,
      digitalFileUrl: ''
    }));
    setDigitalFilePreview(null);
    setUploadProgress(0);
    setUploadError('');
    setUploadeFile(false);
    if (digitalFileInputRef.current) {
      digitalFileInputRef.current.value = '';
    }
  }, []);

 
  // Direct input handlers - memoized to prevent recreation
  const handleNameChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleDescriptionChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, description: e.target.value }));
  }, []);

  const handlePriceChange = useCallback((e) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    const decimalParts = value.split('.');
    
    if (decimalParts.length > 2) return;
    if (decimalParts.length === 2 && decimalParts[1].length > 2) return;
    
    setFormData(prev => ({ ...prev, price: value }));
  }, []);

  const handleCategoryInputChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, category: value }));

    const filtered = categories.filter(cat => 
      cat.collection_name?.toLowerCase().includes(value.toLowerCase()) ||
      cat.collectionName?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCategories(filtered);
    setShowCategoryDropdown(value.length > 0 && filtered.length > 0);
  }, [categories]);

  const handleDigitalProductTypeChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, digitalProductType: e.target.value }));
  }, []);

  const handleDigitalFileUrlChange = useCallback((e) => {
    setFormData(prev => ({ 
      ...prev, 
      digitalFileUrl: e.target.value,
      digitalFile: null // Clear file when URL is entered
    }));
    setDigitalFilePreview(null);
    setUploadProgress(0);
    setUploadError('');
  }, []);




  const handleAllowedDownloadsChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, allowedDownloads: parseInt(e.target.value) || 3 }));
  }, []);

  const handleDownloadExpiryChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, downloadExpiryDays: parseInt(e.target.value) || 30 }));
  }, []);

  const handleWatermarkToggle = useCallback(() => {
    setFormData(prev => ({ ...prev, watermarkEnabled: !prev.watermarkEnabled }));
  }, []);

  // Other handlers remain the same...
  const handleSkuChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, sku: e.target.value }));
  }, []);

  const handleBrandChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, brand: e.target.value }));
  }, []);

  const handleConditionChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, condition: e.target.value }));
  }, []);

  const handleInventoryChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, inventory: e.target.value }));
  }, []);

  const handleWeightChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, weight: e.target.value }));
  }, []);

  const handleDimensionsChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, dimensions: e.target.value }));
  }, []);

  const handleReturnPolicyChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, returnPolicy: e.target.value }));
  }, []);

  const handleUpfrontPaymentChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, upfrontPaymentPercentage: parseInt(e.target.value) }));
  }, []);

  const handleAcceptNegotiationsChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, acceptNegotiations: e.target.checked }));
  }, []);

  // Fixed price change handlers - use refs to maintain focus
  const handleMinPriceChange = useCallback((e) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    const decimalParts = value.split('.');
    
    if (decimalParts.length > 2) return;
    if (decimalParts.length === 2 && decimalParts[1].length > 2) return;
    
    setFormData(prev => ({ ...prev, minPrice: value }));
  }, []);

  const handleMaxPriceChange = useCallback((e) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    const decimalParts = value.split('.');
    
    if (decimalParts.length > 2) return;
    if (decimalParts.length === 2 && decimalParts[1].length > 2) return;
    
    setFormData(prev => ({ ...prev, maxPrice: value }));
  }, []);

  const togglePickup = useCallback(() => {
    setFormData(prev => ({ ...prev, availableForPickup: !prev.availableForPickup }));
  }, []);

  const toggleDelivery = useCallback(() => {
    setFormData(prev => ({ ...prev, availableForDelivery: !prev.availableForDelivery }));
  }, []);

  // Variation handlers - memoized
  const handleVariationTypeChange = useCallback((e) => {
    setCurrentVariation(prev => ({ ...prev, type: e.target.value }));
  }, []);

  const handleVariationValueChange = useCallback((e) => {
    setCurrentVariation(prev => ({ ...prev, value: e.target.value }));
  }, []);

  const handleVariationPriceChange = useCallback((e) => {
    setCurrentVariation(prev => ({ ...prev, price: e.target.value }));
  }, []);

  const handleVariationInventoryChange = useCallback((e) => {
    setCurrentVariation(prev => ({ ...prev, inventory: e.target.value }));
  }, []);

  const addVariation = useCallback(() => {
    if (!currentVariation.type || !currentVariation.value) {
      showToast('Please fill in variation type and value', 'error');
      return;
    }

    const newVariation = {
      ...currentVariation,
      id: Date.now()
    };

    setVariations(prev => [...prev, newVariation]);
    setCurrentVariation({
      type: '',
      value: '',
      price: '',
      inventory: '',
      sku: ''
    });
  }, [currentVariation]);

  const removeVariation = useCallback((id) => {
    setVariations(prev => prev.filter(v => v.id !== id));
  }, []);

  // Form submission - updated to use imageUrls instead of files
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Check if digital file is still uploading
    if (isUploading) {
      showToast('Please wait for the digital file to finish uploading', 'error');
      return;
    }

    // Check if images are still uploading
    if (isUploadingImages) {
      showToast('Please wait for all images to finish uploading', 'error');
      return;
    }

    // Validate digital product requirements
    if (productType === PRODUCT_TYPES.DIGITAL) {
      if (!formData.digitalFileUrl && !formData.digitalFile) {
        showToast('Digital products require either a file upload or an external download URL', 'error');
        return;
      }
    }

    // Validate physical product has images
    if (productType === PRODUCT_TYPES.PHYSICAL && formData.imageUrls.length === 0) {
      showToast('Please upload at least one product image', 'error');
      return;
    }

    setIsLoading(true);

    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('description', formData.description);
    submitData.append('price', formData.price);
    submitData.append('category', formData.category);
    submitData.append('delivery_timeline', formData.deliveryTimeline);
    submitData.append('inventory', formData.inventory);
    submitData.append('sku', formData.sku);
    submitData.append('weight', formData.weight);
    submitData.append('dimensions', formData.dimensions);
    submitData.append('brand', formData.brand);
    submitData.append('condition', formData.condition);
    submitData.append('return_policy', formData.returnPolicy);
    submitData.append('variations', JSON.stringify(variations));
    submitData.append('listing_type', listingType);

    // Append image URLs instead of files
    formData.imageUrls.forEach(url => {
      submitData.append('image_urls', url);
    });

    // Digital product fields
    if (productType === PRODUCT_TYPES.DIGITAL) {
      submitData.append('is_digital', 'true');
      submitData.append('digital_product_type', formData.digitalProductType);
      submitData.append('allowed_downloads', formData.allowedDownloads);
      submitData.append('download_expiry_days', formData.downloadExpiryDays);
      submitData.append('watermark_enabled', formData.watermarkEnabled);
      
      if (formData.digitalFile && !formData.digitalFileUrl) {
        submitData.append('digital_file', formData.digitalFile);
      }
    } else {
      submitData.append('is_digital', 'false');
    }

    if (listingType === LISTING_TYPES.SERVICE) {
      submitData.append('upfront_payment_percentage', formData.upfrontPaymentPercentage);
    }

    if (listingType === LISTING_TYPES.PRODUCT && productType === PRODUCT_TYPES.PHYSICAL) {
      submitData.append('accept_negotiations', formData.acceptNegotiations);
      submitData.append('min_price', formData.minPrice);
      submitData.append('max_price', formData.maxPrice);
      submitData.append('available_for_pickup', formData.availableForPickup);
      submitData.append('available_for_delivery', formData.availableForDelivery);
      submitData.append('delivery_option', formData.deliveryOption);
      submitData.append('lagos_delivery_fee', formData.lagosDeliveryFee || '');
      submitData.append('outside_lagos_delivery_fee', formData.outsideLagosDeliveryFee || '');
    }

    if (productType === PRODUCT_TYPES.DIGITAL && formData.digitalFileUrl) {
      submitData.append('digital_file_url', formData.digitalFileUrl);
      submitData.append('cloudinary_public_id', formData.cloudinaryPublicId);
      submitData.append('file_size', formData.fileSize);
      submitData.append('file_type', formData.fileType);
    }

    try {
      const endpoint = '/save-product';
      const response = await api.post(endpoint, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        const successMessage = productType === PRODUCT_TYPES.DIGITAL 
          ? 'Digital product created successfully'
          : `${listingType === LISTING_TYPES.SERVICE ? 'Service' : 'Product'} created successfully`;
        
        showToast(successMessage, 'success');
        resetForm();
        onProductCreated?.();
      } else {
        throw new Error(response.data.message || `Failed to create ${listingType}`);
      }
    } catch (error) {
      console.error(`Error creating ${listingType}:`, error);
      showToast(error.response?.data?.message || `Failed to create ${listingType}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [formData, variations, listingType, productType, onProductCreated, isUploading, isUploadingImages]);

  // Reset form function
// Fix the resetForm function - remove dependencies that cause re-renders
const resetForm = useCallback(() => {
  // Clean up all object URLs when resetting
  const previewsToCleanup = [...imagePreviews];
  const digitalPreviewToCleanup = digitalFilePreview;

  previewsToCleanup.forEach(preview => URL.revokeObjectURL(preview.previewUrl));
  if (digitalPreviewToCleanup) {
    URL.revokeObjectURL(digitalPreviewToCleanup);
  }

  setFormData({ 
    name: '', 
    description: '', 
    price: '', 
    category: '', 
    deliveryTimeline: '', 
    inventory: '',
    sku: '',
    weight: '',
    dimensions: '',
    brand: '',
    condition: 'new',
    returnPolicy: '7',
    imageUrls: [],
    upfrontPaymentPercentage: 0,
    acceptNegotiations: false,
    minPrice: '',
    maxPrice: '',
    availableForPickup: true,
    availableForDelivery: false,
    deliveryOption: '',
    lagosDeliveryFee: '',
    outsideLagosDeliveryFee: '',
    isDigital: false,
    digitalProductType: '',
    digitalFile: null,
    digitalFileUrl: '',
    allowedDownloads: 3,
    downloadExpiryDays: 30,
    watermarkEnabled: false
  });
  setVariations([]);
  setImagePreviews([]);
  setUploadProgresses({});
  setUploadErrors({});
  setIsUploadingImages(false);
  setDigitalFilePreview(null);
  setUploadProgress(0);
  setUploadError('');
  setIsUploading(false);
  setUploadeFile(false);
  
  // Reset file inputs
  if (digitalFileInputRef.current) {
    digitalFileInputRef.current.value = '';
  }
}, []); // Empty dependency array since we're using refs for cleanup
  // Effects (same as before)
// Fixed effects to prevent infinite re-renders
const initialLoad = useRef(true);

useEffect(() => {
  if (initialLoad.current) {
    initialLoad.current = false;
    return;
  }

  if (listingType === LISTING_TYPES.PRODUCT && !productType) {
    resetForm();
  }
}, [listingType, productType]);

// Separate effect for fetching categories
useEffect(() => {
  if (listingType && categories.length === 0) {
    fetchCategories();
  }
}, [listingType, categories.length]);

// Click outside handlers - this one is fine
useEffect(() => {
  const handleClickOutside = (event) => {
    if (timelineRef.current && !timelineRef.current.contains(event.target)) {
      setShowTimelineDropdown(false);
    }
    if (categoryInputRef.current && !categoryInputRef.current.contains(event.target)) {
      setShowCategoryDropdown(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

// Cleanup effect - this one is fine
useEffect(() => {
  return () => {
    imagePreviews.forEach(preview => URL.revokeObjectURL(preview.previewUrl));
    if (digitalFilePreview) {
      URL.revokeObjectURL(digitalFilePreview);
    }
  };
}, [imagePreviews, digitalFilePreview]);


  // Digital Product Settings Component with enhanced file upload
  const DigitalProductSettings = () => (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border border-purple-200 p-6">
      <div className="flex items-center mb-8">
        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
          <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-purple-900">Digital Product Settings</h3>
          <p className="text-purple-700 text-sm">Configure your digital download</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Digital Product Type */}
        <div>
          <label className="block text-sm font-medium text-purple-800 mb-3">
            Product Type
          </label>
          <select
            className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-700 focus:border-purple-700 transition-all"
            value={formData.digitalProductType}
            onChange={handleDigitalProductTypeChange}
            required
          >
            <option value="">Select product type</option>
            <option value={DIGITAL_PRODUCT_TYPES.EBOOK}>eBook</option>
            <option value={DIGITAL_PRODUCT_TYPES.ART_COLLECTION}>Art Collection</option>
            <option value={DIGITAL_PRODUCT_TYPES.OTHER}>Other Digital Product</option>
          </select>
        </div>

        {/* File Upload Section */}
        <div className="bg-white rounded-xl border border-purple-200 p-6">
          <label className="block text-sm font-medium text-purple-800 mb-4">
            Digital File
          </label>
          
          <div className="space-y-4">
            {/* File Upload Option */}
            <div>
              <label className="block text-sm text-purple-700 mb-2">Upload File</label>
              <div className={`relative border-2 border-dashed rounded-lg p-4 transition-all duration-200 ${
                uploadError 
                  ? 'border-red-300 bg-red-50' 
                  : uploadProgress > 0 && uploadProgress < 100
                    ? 'border-purple-300 bg-purple-50'
                    : 'border-gray-300 bg-white hover:border-purple-400 hover:bg-purple-50'
              }`}>
                <input
                  ref={digitalFileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.zip"
                  onChange={handleDigitalFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={uploadProgress > 0 && uploadProgress < 100}
                />
                <div className="text-center">
                  <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-900">
                      {uploadProgress > 0 && uploadProgress < 100 ? 'Uploading...' : 'Click to upload'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, images (JPEG, PNG, GIF, WebP), or ZIP files. Max 100MB.
                    </p>
                  </div>
                </div>
              </div>

              {/* Upload Progress */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="space-y-2 mt-3">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-purple-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-600 text-center">
                    Uploading... {uploadProgress}%
                  </p>
                </div>
              )}

              {/* Upload Error Message */}
              {uploadError && (
                <div className="flex items-center bg-red-50 border border-red-200 rounded-lg px-4 py-3 mt-3">
                  <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-700 text-sm font-medium">{uploadError}</span>
                </div>
              )}

              {/* Upload Success */}
              {uploadProgress === 100 && (
                <div className="flex items-center bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mt-3">
                  <svg className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-blue-700 text-sm font-medium">File uploaded successfully!</span>
                </div>
              )}
            </div>

            {/* OR Separator */}
            <div className="flex items-center">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="px-4 text-sm text-gray-500">OR</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>

            {/* External URL Option */}
            <div>
              <label className="block text-sm text-purple-700 mb-2">External Download URL</label>
              <input
                type="url"
                className={`w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-700 focus:border-purple-700 transition-all ${hasUploadedFile ? "bg-purple-100 text-purple-700 hidden" : ""}`}
                value={formData.digitalFileUrl}
                onChange={handleDigitalFileUrlChange}
                placeholder="https://example.com/download/file.pdf"
                disabled={isUploading || hasUploadedFile}
              />
              {!hasUploadedFile ? (
                 <p className="text-gray-500 mt-2 text-sm">
                Provide a direct download link to your file
              </p>
              ) : (
                <></>
              )}
        
            </div>
          </div>

          {/* File Preview */}
          {(formData.digitalFile || formData.digitalFileUrl) && (
            <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {digitalFilePreview ? (
                    <img
                      src={digitalFilePreview}
                      alt="File preview"
                      className="w-12 h-12 object-cover rounded-lg mr-3"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-purple-900">
                      {formData.digitalFile?.name || 'External File'}
                    </p>
                    <p className="text-xs text-purple-700">
                      {formData.digitalFile ? `${(formData.digitalFile.size / (1024 * 1024)).toFixed(2)} MB` : 'External download'}
                    </p>
                    {formData.digitalFileUrl && (
                      <p className="text-xs text-purple-600 truncate max-w-xs">
                        {formData.digitalFileUrl}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeDigitalFile}
                  className="text-red-600 hover:text-red-800 transition-colors"
                  disabled={isUploading}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Download Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-purple-800 mb-3">
              Allowed Downloads
            </label>
            <select
              className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-700 focus:border-purple-700 transition-all"
              value={formData.allowedDownloads}
              onChange={handleAllowedDownloadsChange}
            >
              {[1, 2, 3, 5, 10, 20].map(num => (
                <option key={num} value={num}>{num} download{num !== 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-800 mb-3">
              Download Expiry
            </label>
            <select
              className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-700 focus:border-purple-700 transition-all"
              value={formData.downloadExpiryDays}
              onChange={handleDownloadExpiryChange}
            >
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
              <option value="365">1 year</option>
            </select>
          </div>
        </div>

        {/* Watermark Option for Images */}
        {formData.digitalProductType === DIGITAL_PRODUCT_TYPES.ART_COLLECTION && (
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-purple-200">
            <div>
              <h4 className="text-sm font-medium text-purple-800">Enable Watermark</h4>
              <p className="text-sm text-purple-600">Add protection to your images</p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.watermarkEnabled}
                onChange={handleWatermarkToggle}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-700"></div>
            </label>
          </div>
        )}
      </div>
    </div>
  );

  // Service-specific settings component
  const ServiceSettings = () => (
    <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-blue-900">Service Settings</h3>
          <p className="text-blue-700 text-sm">Configure payment terms for your service</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-blue-800 mb-4">
            Upfront Payment Percentage
          </label>
          <div className="space-y-4">
            <input
              type="range"
              className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
              value={formData.upfrontPaymentPercentage}
              onChange={handleUpfrontPaymentChange}
              min="0"
              max="100"
              step="10"
            />
            <div className="flex justify-between text-blue-700 text-sm">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>
          <p className="text-blue-600 mt-3 text-sm">
            Set the percentage of payment required upfront to secure this service
          </p>
        </div>

        <div className="bg-white rounded-lg border border-blue-300 p-6 text-center">
          <div className="text-3xl font-bold text-blue-800 mb-2">
            {formData.upfrontPaymentPercentage}%
          </div>
          <div className="text-blue-700 font-medium">Upfront Payment</div>
          <div className="text-blue-600 mt-2 text-sm">
            Required to book this service
          </div>
        </div>
      </div>
    </div>
  );
  // Updated ImageUpload component usage
  const ImageUpload = () => (
    <ImageUploadWithProgress
      imagePreviews={imagePreviews}
      uploadProgresses={uploadProgresses}
      uploadErrors={uploadErrors}
      isUploadingImages={isUploadingImages}
      handleImageChange={handleImageChange}
      removeImage={removeImage}
    />
  );

  if (listingType === LISTING_TYPES.SCAN_PRODUCT) {
    const backButton = (
      <div className="text-center mb-4 md:mb-6">
        <button
          type="button"
          onClick={() => setListingType(LISTING_TYPES.PRODUCT)}
          className="flex items-center text-gray-600 hover: transition-colors text-sm mx-auto"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Try Manual Upload
        </button>
      </div>
    );
    
    return <ScanProduct backButton={backButton} onRequestBack={() => setListingType(LISTING_TYPES.PRODUCT)} />;
  }

  if (listingType === LISTING_TYPES.BULK_UPLOAD) {
    const backButton = (
      <div className="text-center mb-4 md:mb-6">
        <button
          type="button"
          onClick={() => setListingType(LISTING_TYPES.PRODUCT)}
          className="flex items-center text-gray-600 hover: transition-colors text-sm mx-auto"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Try Manual Upload
        </button>
      </div>
    );
    
    return <BulkUploadFlow backButton={backButton} />;
  }

  // Main Form Component
  return (
    <>
    <div className="p-4 sm:p-6 lg:p-6 lg:h-full lg:flex lg:flex-col">
      <div className="max-w-4xl mx-auto lg:w-full lg:flex lg:flex-col lg:h-full lg:min-h-0">
        {/* TOP: Sticky header + pills + empty states */}
        <div className="lg:shrink-0">
        {/* Inline Pills + Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-end mb-6">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              aria-label="Go back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {/* Listing Type Pills */}
          <div className="flex justify-center gap-3">
            <button
              type="button"
              onClick={() => { setListingType(LISTING_TYPES.PRODUCT); setProductType(null); }}
              className={`flex items-center gap-2.5 px-6 md:px-8 py-3 md:py-3.5 rounded-full font-semibold text-sm transition-all duration-200 ${
                listingType === LISTING_TYPES.PRODUCT
                  ? 'bg-blue-600 text-white scale-105'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
              Product
            </button>
            <button
              type="button"
              onClick={() => { setListingType(LISTING_TYPES.SERVICE); setProductType(null); }}
              className={`flex items-center gap-2.5 px-6 md:px-8 py-3 md:py-3.5 rounded-full font-semibold text-sm transition-all duration-200 ${
                listingType === LISTING_TYPES.SERVICE
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white scale-105'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
              Service
            </button>
          </div>

          {/* Product Sub-type: Physical / Digital pills */}
          {listingType === LISTING_TYPES.PRODUCT && (
            <div className="flex justify-center gap-2 mt-4">
              <button
                type="button"
                onClick={() => setProductType(PRODUCT_TYPES.PHYSICAL)}
                className={`flex items-center gap-2 px-5 md:px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-200 ${
                  productType === PRODUCT_TYPES.PHYSICAL
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-500 border border-gray-300 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                </svg>
                Physical
              </button>
              <button
                type="button"
                onClick={() => setProductType(PRODUCT_TYPES.DIGITAL)}
                className={`flex items-center gap-2 px-5 md:px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-200 ${
                  productType === PRODUCT_TYPES.DIGITAL
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-500 border border-gray-300 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                Digital
              </button>
            </div>
          )}

          {/* Scan CTA — prominent, full-width, always visible when Product is selected */}
          {listingType === LISTING_TYPES.PRODUCT && (
            <div className="mt-5">
              <button
                type="button"
                onClick={() => setShowScanChoice(true)}
                className="relative w-full group overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 p-0.5 transition-all duration-300 active:scale-[0.98]"
              >
                <div className="relative flex items-center justify-between w-full rounded-2xl bg-white px-5 py-4 md:py-5 transition-all duration-300 group-hover:bg-emerald-50/80">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                    </div>
                    <div className="text-left">
                      <span className="block text-base md:text-lg font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">
                        Snap or Upload Photo
                      </span>
                      <span className="block text-sm text-gray-500 group-hover:text-emerald-600 transition-colors">
                        AI-powered — create a listing in seconds
                      </span>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-1 text-emerald-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    Try it
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </div>
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Empty state when nothing selected */}
        {!listingType && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
            </div>
            <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-2">
              What are you selling today?
            </h2>
            <p className="text-gray-500 max-w-md mx-auto text-sm md:text-base">
              Choose <span className="font-semibold text-blue-600">Product</span> to sell physical or digital goods, or{' '}
              <span className="font-semibold text-purple-600">Service</span> to offer your skills and expertise.
            </p>
          </div>
        )}

        {/* Product-type-not-selected empty state */}
        {listingType === LISTING_TYPES.PRODUCT && !productType && (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-300 mb-8">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-700 mb-1">
              What kind of product?
            </h3>
            <p className="text-sm text-gray-500">
              Choose <span className="font-medium text-blue-600">Physical</span>,{' '}
              <span className="font-medium text-blue-600">Digital</span>, or{' '}
              <span className="font-medium text-green-600">Scan</span> above to continue
            </p>
          </div>
        )}

        </div>
        {/* BOTTOM: Scrollable form on desktop */}
        <div className="lg:overflow-y-auto lg:flex-1 lg:min-h-0">
        {/* Form shown only when enough selections are made */}
        {(listingType === LISTING_TYPES.SERVICE || (listingType === LISTING_TYPES.PRODUCT && productType)) && (
        <>

        {/* Specific Settings Section */}

        {listingType === LISTING_TYPES.SERVICE && <ServiceSettings />}
        {listingType === LISTING_TYPES.PRODUCT && productType === PRODUCT_TYPES.PHYSICAL && (
          <ProductSettings
            formData={formData}
            handleAcceptNegotiationsChange={handleAcceptNegotiationsChange}
            handleMinPriceChange={handleMinPriceChange}
            handleMaxPriceChange={handleMaxPriceChange}
            togglePickup={togglePickup}
            toggleDelivery={toggleDelivery}
          />
        )}
        {listingType === LISTING_TYPES.PRODUCT && productType === PRODUCT_TYPES.DIGITAL && <DigitalProductSettings />}

        <form onSubmit={handleSubmit} className="space-y-8 pt-4">
          {/* General Information Section */}
          <div className="bg-white rounded-xl p-6 space-y-6">
            <h2 className="text-xl font-semibold mb-5">
              General Information
            </h2>
            
            {/* Name and Price */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {listingType === LISTING_TYPES.PRODUCT ? 'Product Name' : 'Service Title'}
                </label>
                <input
                  type="text"
                  id="name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.name}
                  onChange={handleNameChange}
                  placeholder={`Enter ${listingType} name`}
                  required
                />
              </div>

              <div>
                <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                  Base Price (₦)
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-500 font-medium">₦</span>
                  <input
                    type="text"
                    id="price"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={formData.price}
                    onChange={handlePriceChange}
                    placeholder="0.00"
                    inputMode="decimal"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="description"
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={formData.description}
                onChange={handleDescriptionChange}
                placeholder={`Provide a detailed description of your ${listingType}`}
                required
              ></textarea>
            </div>

            {/* Category and Delivery Timeline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Category Dropdown/Input */}
              <div className="relative" ref={categoryInputRef}>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  id="category"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.category}
                  onChange={handleCategoryInputChange}
                  placeholder="e.g., Electronics, Plumbing"
                  required
                />
                
                {showCategoryDropdown && (
                  <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map((cat, index) => (
                        <div
                          key={cat.id || index}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleCategorySelect(cat.collection_name || cat.collectionName)}
                        >
                          {cat.collection_name || cat.collectionName}
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">
                        No category found. Press Enter to use "{formData.category}"
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Delivery Timeline Dropdown */}
              <div className="relative" ref={timelineRef}>
                <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                  {productType === PRODUCT_TYPES.DIGITAL ? 'Delivery Timeline' : 'Delivery/Completion Timeline'}
                </label>
                <div
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white cursor-pointer flex justify-between items-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  onClick={() => setShowTimelineDropdown(prev => !prev)}
                  role="button"
                  tabIndex={0}
                >
                  <span className={formData.deliveryTimeline ? 'text-gray-900' : 'text-gray-500'}>
                    {selectedTimelineLabel}
                  </span>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
                
                {showTimelineDropdown && (
                  <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {DELIVERY_TIMELINES.map((t) => (
                      <div
                        key={t.value}
                        className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${formData.deliveryTimeline === t.value ? 'bg-blue-50 font-medium' : ''}`}
                        onClick={() => handleTimelineSelect(t.value)}
                      >
                        {t.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Media Section */}
          {(listingType === LISTING_TYPES.SERVICE || 
            (listingType === LISTING_TYPES.PRODUCT && productType === PRODUCT_TYPES.PHYSICAL) ||
            (listingType === LISTING_TYPES.PRODUCT && productType === PRODUCT_TYPES.DIGITAL)) && (
            <ImageUpload />
          )}
          
          {/* Inventory & Shipping/Service Details Section */}
          {(listingType === LISTING_TYPES.SERVICE || 
            (listingType === LISTING_TYPES.PRODUCT && productType === PRODUCT_TYPES.PHYSICAL)) && (
            <div className="bg-white rounded-xl p-6 space-y-6">
              <h2 className="text-xl font-semibold mb-5">
                {listingType === LISTING_TYPES.PRODUCT ? 'Inventory & Shipping Details' : 'Service & Returns Policy'}
              </h2>

              {listingType === LISTING_TYPES.PRODUCT ? (
                // Physical Product Fields
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label htmlFor="inventory" className="block text-sm font-medium text-gray-700 mb-2">Inventory (Stock)</label>
                      <input
                        type="number"
                        id="inventory"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        value={formData.inventory}
                        onChange={handleInventoryChange}
                        placeholder="Qty"
                        min="0"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="sku" className="block text-sm font-medium text-gray-700 mb-2">SKU (Stock Keeping Unit)</label>
                      <input
                        type="text"
                        id="sku"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        value={formData.sku}
                        onChange={handleSkuChange}
                        placeholder="Optional unique ID"
                      />
                    </div>
                    <div>
                      <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                      <input
                        type="text"
                        id="brand"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        value={formData.brand}
                        onChange={handleBrandChange}
                        placeholder="e.g., Apple, Samsung"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label htmlFor="condition" className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                      <select
                        id="condition"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        value={formData.condition}
                        onChange={handleConditionChange}
                      >
                        {CONDITIONS.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="weight" className="block text-sm font-medium text-gray-700 mb-2">Weight (kg)</label>
                      <input
                        type="number"
                        id="weight"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        value={formData.weight}
                        onChange={handleWeightChange}
                        placeholder="e.g., 0.5"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label htmlFor="dimensions" className="block text-sm font-medium text-gray-700 mb-2">Dimensions (L x W x H in cm)</label>
                      <input
                        type="text"
                        id="dimensions"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        value={formData.dimensions}
                        onChange={handleDimensionsChange}
                        placeholder="e.g., 10x10x10"
                      />
                    </div>
                  </div>

                  {/* Return Policy - For Products */}
                  <div>
                    <label htmlFor="returnPolicy" className="block text-sm font-medium text-gray-700 mb-2">Return Policy</label>
                    <select
                      id="returnPolicy"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      value={formData.returnPolicy}
                      onChange={handleReturnPolicyChange}
                    >
                      {RETURN_POLICIES.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                // Service Field (Return Policy is shared)
                <div>
                  <label htmlFor="returnPolicy" className="block text-sm font-medium text-gray-700 mb-2">Cancellation/Refund Policy</label>
                  <select
                    id="returnPolicy"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    value={formData.returnPolicy}
                    onChange={handleReturnPolicyChange}
                  >
                    {RETURN_POLICIES.map(r => (
                      <option key={r.value} value={r.value}>{r.label.replace('return policy', 'refund policy')}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Variation Section (Physical products only) */}
          <VariationsSection
            listingType={listingType}
            currentVariation={currentVariation}
            variations={variations}
            handleVariationTypeChange={handleVariationTypeChange}
            handleVariationValueChange={handleVariationValueChange}
            handleVariationPriceChange={handleVariationPriceChange}
            handleVariationInventoryChange={handleVariationInventoryChange}
            addVariation={addVariation}
            removeVariation={removeVariation}
            formatNumberWithCommas={formatNumberWithCommas}
          />

          {/* Form Submission Button */}
          <div className="pt-5">
            <button
              type="submit"
              className="w-full flex items-center justify-center px-6 py-3 border border-transparent rounded-xl text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              disabled={isLoading || isUploading || isUploadingImages}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading File...
                </>
              ) : isUploadingImages ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading Images...
                </>
              ) : (
                `Create ${productType === PRODUCT_TYPES.DIGITAL ? 'Digital Product' : listingType === LISTING_TYPES.PRODUCT ? 'Product' : 'Service'}`
              )}
            </button>
          </div>
        </form>
        </>
        )}
        </div>
      </div>
    </div>
    
    {/* Scan Choice Modal */}
    {showScanChoice && (
      <div className="fixed inset-0 bg-black/50 z-[500] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-black rounded-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
          <div className="p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Choose Upload Type</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">How would you like to list your products?</p>
            
            <div className="space-y-3">
              <button
                onClick={() => { setShowScanChoice(false); setListingType(LISTING_TYPES.SCAN_PRODUCT); }}
                className="flex items-center gap-4 w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 dark:text-white">Single Product</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Scan one product at a time with AI-powered analysis</div>
                </div>
              </button>
              
              <button
                onClick={() => { setShowScanChoice(false); setListingType(LISTING_TYPES.BULK_UPLOAD); }}
                className="flex items-center gap-4 w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-emerald-200 dark:hover:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 dark:text-white">Bulk Products Upload</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Scan multiple products at once (up to 10)</div>
                </div>
              </button>
            </div>
          </div>
          
          <div className="border-t border-gray-200 dark:border-gray-800 p-4">
            <button
              onClick={() => setShowScanChoice(false)}
              className="w-full py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CreateProduct;